/**
 * JoinFundraiserForm.tsx - Promoter Registration Form Component
 *
 * This component provides the registration form for new promoters to join
 * fundraisers with contact information, goal setting, and onboarding.
 *
 * Key Features:
 * - Promoter registration form with validation
 * - Contact information collection and formatting
 * - Personal goal setting and expectations
 * - Form validation with real-time feedback
 * - Success handling and immediate onboarding
 * - Mobile-optimized form design
 *
 * Form Fields:
 * - First Name and Last Name (required)
 * - Email Address with validation
 * - Phone Number with formatting
 * - Personal Fundraising Goal (optional)
 * - Motivation and personal message
 *
 * Validation Features:
 * - Required field validation
 * - Email format validation
 * - Phone number formatting and validation
 * - Goal amount validation and formatting
 * - Real-time error feedback
 *
 * Onboarding Process:
 * - Immediate promoter account creation
 * - Welcome messaging and next steps
 * - Access to promotional tools and materials
 * - Dashboard redirect for new promoters
 * - Confirmation and success messaging
 *
 * Props: None (uses URL parameters and MobX store)
 *
 * Dependencies: Form validation, phone/email formatters, MobX store
 */

import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { Heart } from "lucide-react";
import { formatPhoneNumber, isValidEmail } from "../../utils/formatters";
import { fundraiserStore } from "../../stores/FundraiserStore";
import { lightenColor } from "../../utils/colorUtils";
import {
  CountryDropdown,
  CountryPhoneDropdown,
  CountryWithStateModel,
} from "@mindmemobile/mm-payment-modules";
import {
  CoreInputModelUtil,
  getNewErrors,
  InputErrorModel,
  SingleSelect,
} from "@mindmemobile/mm-shared-global";
import { autorun } from "mobx";
import { IAPIJoinFundRaiserRequest } from "../../interfaces";
import { FundraiserService } from "../../services";
import { Toast } from "../Toast";
import { FundraiserDonationButtonColors } from "../../models";
import ToastService from "../ToastService";

const FormContainer = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f5f9;
`;

const FormHeader = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const FormTitle = styled.h2`
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const FormSubtitle = styled.p`
  font-size: 16px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
  max-width: 380px;
  margin: 0 auto;
`;

const FormBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormRow = styled.div`
  display: grid;
  gap: 16px;
  margin-bottom: 0;
`;

const NameRow = styled(FormRow)`
  grid-template-columns: 1fr 1fr;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ZipCityRow = styled(FormRow)`
  grid-template-columns: 0.7fr 1.3fr;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StateCountryRow = styled(FormRow)`
  grid-template-columns: 0.7fr 1.3fr;
  .invite-promter-state-dpdn {
    .header {
      padding: 14px 16px;
      border: 2px solid rgb(226, 232, 240);
      border-radius: 12px;
      font-size: 16px;
      color: rgb(30, 41, 59);
      transition: border-color 0.2s;
      background: rgb(255, 255, 255);
      height: 56px;
    }
    .select-header-span {
      color: #9ca3af;
      width: calc(100% - 30px);
    }
    .select-header-icon {
      height: 46px;
      color: #9ca3af;
      border-left: 1px solid #ccc;
    }
    &.has-value .select-header-span {
      color: #1f2937;
    }
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  &.donation-country-dropdown
    .country-phone-wrapper
    .country-dropdown-expanded {
    > div {
      padding: 14px 16px;
      border: 2px solid rgb(226, 232, 240);
      border-radius: 12px;
      font-size: 16px;
      color: rgb(30, 41, 59);
      transition: border-color 0.2s;
      background: rgb(255, 255, 255);
      height: 55px;
      .dropdown-menu {
        width: calc(100% + 29px) !important;
        transform: translate(-16px, 38px) !important;
      }
      .styled-dropdown-flex .custom-label {
        color: #9ca3af;
        font-size: 16px;
      }
    }
    &.has-value > div .styled-dropdown-flex .custom-label {
      color: #1f2937;
    }
  }
`;

const FullWidthFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  .invite-promter-country-phone-dpdn {
    > div > div {
      padding: 14px 16px;
      border: 2px solid rgb(226, 232, 240);
      border-radius: 12px;
      font-size: 16px;
      color: rgb(30, 41, 59);
      transition: border-color 0.2s;
      background: rgb(255, 255, 255);
      .custom-label {
        font-size: 16px;
        color: #1e293b;
      }
      input {
        font-size: 16px;
        color: #1e293b;
        width: 100%;
      }
      .dropdown-menu {
        min-width: 320px;
        width: auto !important;
        transform: translate(-15px, 38px) !important;
      }
    }
  }
`;

interface InputProps {
  hasError?: boolean;
}

const FormInput = styled.input<InputProps>`
  padding: 14px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;
  border-color: ${({ hasError }) =>
    hasError ? "#ef4444" : "#e2e8f0"}; // red if error

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const PhoneInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const PhonePrefix = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  font-weight: 600;
  color: #64748b;
  z-index: 1;
`;

const PhoneInput = styled.input<{ hasError: boolean }>`
  padding: 14px 16px 14px 48px;
  border: 2px solid ${(props) => (props.hasError ? "#dc2626" : "#e2e8f0")};
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? "#dc2626" : "#10b981")};
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.hasError ? "rgba(220, 38, 38, 0.1)" : "rgba(16, 185, 129, 0.1)"};
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const EmailInput = styled.input<{ hasError: boolean }>`
  padding: 14px 16px;
  border: 2px solid ${(props) => (props.hasError ? "#dc2626" : "#e2e8f0")};
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  transition: border-color 0.2s ease;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? "#dc2626" : "#10b981")};
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.hasError ? "rgba(220, 38, 38, 0.1)" : "rgba(16, 185, 129, 0.1)"};
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const CountrySelect = styled.select`
  padding: 14px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }
`;

const StateSelect = styled.select`
  padding: 14px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  color: #1e293b;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 8px 0 8px 0;
`;

const CheckboxItem = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  font-size: 16px;
  color: #1e293b;
  line-height: 1.4;
`;

const Checkbox = styled.input<{ checked?: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 2px solid ${(props) => (props.checked ? "#10b981" : "#e2e8f0")};
  background: ${(props) => (props.checked ? "#10b981" : "#ffffff")};
  appearance: none;
  margin: 0;
  flex-shrink: 0;
  margin-top: 2px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:checked::after {
    content: "✓";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #ffffff;
    font-size: 12px;
    font-weight: 700;
  }

  &:hover {
    border-color: #10b981;
  }
`;

const DisclaimerText = styled.div`
  font-size: 14px;
  color: #64748b;
  line-height: 1.5;
  margin-left: 32px;
  margin-top: 4px;
  margin-bottom: 8px;
`;

const JoinButton = styled.button`
  width: 50%;
  padding: 18px 32px;
  border: none;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 8px;

  &:hover {
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;

    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
`;

export class JoinFormData {
  firstName: string = "";
  lastName: string = "";
  streetAddress: string = "";
  zip: string = "";
  city: string = "";
  state: string = "";
  country: string = "";
  email: string = "";
  phone: string = "";
  phoneCountryId: string = "";
  phoneCountryCode: string = "";
  isAnonymous: boolean = false;
  receiveNotifications: boolean = true;
  countryDetails: CountryWithStateModel;
  errors: InputErrorModel<JoinFormData>[] = [];
  constructor(init?: Partial<JoinFormData>) {
    this.city = init?.city || "";
    this.state = init?.state || "";
    this.zip = init?.zip || "";
    this.firstName = init?.firstName || "";
    this.lastName = init?.lastName || "";
    this.streetAddress = init?.streetAddress || "";
    this.country = init?.country || "";
    this.email = init?.email || "";
    this.phone = init?.phone || "";
    this.phoneCountryId = init?.phoneCountryId || "";
    this.phoneCountryCode = init?.phoneCountryCode || "";
    this.isAnonymous = init?.isAnonymous || false;
    this.receiveNotifications = init?.receiveNotifications ?? true;
    this.errors =
      (init &&
        init.errors?.map((e) => {
          return new InputErrorModel<JoinFormData>(e);
        })) ||
      [];
    this.countryDetails = init?.countryDetails || new CountryWithStateModel();
    autorun(() => {
      this.validate(true);
    });
  }

  hasError(field: keyof JoinFormData): boolean {
    return this.errors.some((e) => e.field === field);
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<JoinFormData>[] = [];
    CoreInputModelUtil.validateTextField(
      this.firstName,
      "firstName",
      newErrors
    );
    CoreInputModelUtil.validateTextField(this.firstName, "lastName", newErrors);
    CoreInputModelUtil.validateTextField(this.phone, "phone", newErrors);
    CoreInputModelUtil.validateTextField(this.country, "country", newErrors);
    CoreInputModelUtil.validateTextField(this.state, "state", newErrors);
    CoreInputModelUtil.validateTextField(this.city, "city", newErrors);
    CoreInputModelUtil.validateTextField(
      this.streetAddress,
      "streetAddress",
      newErrors
    );
    CoreInputModelUtil.validateTextField(this.zip, "zip", newErrors);
    CoreInputModelUtil.validateTextField(
      this.phoneCountryId,
      "phoneCountryId",
      newErrors
    );

    CoreInputModelUtil.validateEmail("email", this.email, newErrors);

    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0;
  }

  createAPIRequest(
    accountId: string,
    fundraiserId: string
  ): IAPIJoinFundRaiserRequest {
    return {
      company: "",
      fundRaiserId: fundraiserId,
      firstName: this.firstName,
      lastName: this.lastName,
      address: {
        country: this.countryDetails.name,
        countryId: this.countryDetails.id,
        city: this.city,
        zipCode: this.zip,
        countryCode: this.countryDetails.code.toString(),
        stateId: this.state,
        state: this.state,
        address1: this.streetAddress,
        address2: "",
        address3: "",
      },
      email: this.email,
      mobilePhone: {
        countryCode: this.phoneCountryCode.toString(),
        countryId: this.phoneCountryId,
        phoneNumber: this.phone,
      },
      accountId: accountId,
    };
  }
}

interface JoinFundraiserFormProps {
  countriesWithState: CountryWithStateModel[];
  fundraiserId: string;
  accountId: string;
}

export const JoinFundraiserForm: React.FC<JoinFundraiserFormProps> = observer(
  ({ countriesWithState, accountId, fundraiserId }) => {
    const [formData, setFormData] = useState<JoinFormData>(
      new JoinFormData({
        country: countriesWithState.find((c) => c.getUSCountry())?.id || "",
        phoneCountryId:
          countriesWithState.find((c) => c.getUSCountry())?.id || "",
        phoneCountryCode:
          countriesWithState.find((c) => c.getUSCountry())?.code.toString() ||
          "",
      })
    );
    useEffect(() => {
      setFormData((prev) => {
        const countryDetail =
          countriesWithState.find((c) => c.id === prev.country) ||
          countriesWithState.find((c) => c.getUSCountry());
        return new JoinFormData({
          ...prev,
          countryDetails: countryDetail,
          country: countriesWithState.find((c) => c.getUSCountry())?.id || "",
          phoneCountryId:
            countriesWithState.find((c) => c.getUSCountry())?.id || "",
          phoneCountryCode:
            countriesWithState.find((c) => c.getUSCountry())?.code.toString() ||
            "",
        });
      });
    }, [fundraiserStore.countryStore.countryDataLoaded]);
    const updateFormData = (
      field: keyof JoinFormData,
      value: string | boolean | any
    ) => {
      setFormData((prev) => {
        const updatedInfo = new JoinFormData({
          ...prev,
          [field]: value,
        });
        updatedInfo.validate(true);

        return updatedInfo;
      });
    };

    const isFormValid = () => {
      return formData.validate(false);
    };

    // Get join button colors from donation button design
    const donationButtonType =
      fundraiserStore.builderData?.donationButtonType || "gradient";
    const donationButtonColors =
      fundraiserStore.builderData?.donationButtonColors ||
      new FundraiserDonationButtonColors();

    const getJoinButtonStyles = () => {
      const background =
        donationButtonType === "solid"
          ? donationButtonColors.primary.hex
          : `linear-gradient(135deg, ${donationButtonColors.primary.hex}, ${donationButtonColors.secondary.hex})`;

      return {
        background,
        color: donationButtonColors.text.rgba || "#ffffff",
        boxShadow: `0 4px 16px ${donationButtonColors.primary.rgba}30`,
        "--hover-shadow": `0 8px 24px ${donationButtonColors.primary.rgba}40`,
      } as React.CSSProperties & { "--hover-shadow": string };
    };

    const countryStates = (
      countriesWithState.find((c) => c.id === formData.country)?.states || []
    )
      .map((state) => {
        return {
          label: state.name,
          value: state.id,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    function onSubmitClick() {
      if (!isFormValid()) {
        setFormData(new JoinFormData({ ...formData }));
        return;
      }

      const request = formData.createAPIRequest(accountId, fundraiserId);

      FundraiserService.addNewPromoterToFundRaiser(request).subscribe(
        (response) => {
          if (response.result?.contactId) {
            ToastService.showSuccess("Thank you for joining our fundraiser!");
            // Reset form data
            setFormData(
              new JoinFormData({
                country:
                  countriesWithState.find((c) => c.getUSCountry())?.id || "",
                phoneCountryId:
                  countriesWithState.find((c) => c.getUSCountry())?.id || "",
              })
            );
            window.open(
              `${fundraiserStore.getFundRaiserDomainBaseURL()}/fundraiser/promoter-dashboard/${fundraiserId}?PID=${response.result?.contactId}`,
              "_self"
            );
          } else {
            ToastService.showError(
              "An error occurred while submitting the form. Please try again later."
            );
          }
        }
      );
    }
    return (
      <FormContainer data-join-form>
        <FormHeader>
          <FormTitle>
            <Heart size={24} color="#10b981" />
            We Can Make a Difference Together
          </FormTitle>
          <FormSubtitle>
            If you are passionate about what we are doing, we invite you to join
            us and help spread the word!
          </FormSubtitle>
        </FormHeader>

        <FormBody>
          <NameRow>
            <FormGroup>
              <FormInput
                type="text"
                placeholder="First Name"
                value={formData.firstName}
                onChange={(e) => updateFormData("firstName", e.target.value)}
                hasError={formData.hasError("firstName")}
              />
            </FormGroup>
            <FormGroup>
              <FormInput
                type="text"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={(e) => updateFormData("lastName", e.target.value)}
                hasError={formData.hasError("lastName")}
              />
            </FormGroup>
          </NameRow>

          <FullWidthFormGroup>
            <FormInput
              type="text"
              placeholder="Street Address"
              value={formData.streetAddress}
              onChange={(e) => updateFormData("streetAddress", e.target.value)}
              hasError={formData.hasError("streetAddress")}
            />
          </FullWidthFormGroup>

          <ZipCityRow>
            <FormGroup>
              <FormInput
                type="text"
                placeholder="Zip"
                value={formData.zip}
                onChange={(e) => updateFormData("zip", e.target.value)}
                hasError={formData.hasError("zip")}
              />
            </FormGroup>
            <FormGroup>
              <FormInput
                type="text"
                placeholder="City"
                value={formData.city}
                onChange={(e) => updateFormData("city", e.target.value)}
                hasError={formData.hasError("city")}
              />
            </FormGroup>
          </ZipCityRow>

          <StateCountryRow>
            <FormGroup>
              <SingleSelect
                selectedKey={formData.state}
                className={`default-padding-search-dpdn invite-promter-state-dpdn ${
                  formData.state ? "has-value" : ""
                }`}
                options={[countryStates || []]}
                nameKeys={["label"]}
                valueKeys={["value"]}
                hasError={formData.hasError("state")}
                placeholder="Select State"
                searchable={true}
                errorMessage="Select State"
                onChange={(option: any) =>
                  updateFormData("state", option.value)
                }
              />
            </FormGroup>
            <FormGroup className="donation-country-dropdown">
              <CountryDropdown
                className={`country-dropdown country-dropdown-expanded invite-promter-country-dpdn ${
                  formData.country ? "has-value" : ""
                }`}
                selectedCountry={
                  countriesWithState.find((c) => c.id === formData.country) ||
                  new CountryWithStateModel()
                }
                // selectedCountry={new CountryWithStateModel()}
                error={formData.hasError("country")}
                errorMessage={"Please select country"}
                onCountryChange={(id: string) => {
                  updateFormData("country", id);
                  updateFormData(
                    "countryDetails",
                    countriesWithState.find((c) => c.id === id) ||
                      new CountryWithStateModel()
                  );
                  if (id != formData.country) {
                    updateFormData("state", "");
                  }
                }}
                availableCountries={countriesWithState}
              />
            </FormGroup>
          </StateCountryRow>

          <FullWidthFormGroup>
            <FormInput
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={(e) => updateFormData("email", e.target.value)}
              //onBlur={handleEmailBlur}
              hasError={formData.hasError("email")}
            />
          </FullWidthFormGroup>

          <FullWidthFormGroup>
            <CountryPhoneDropdown
              className="invite-promter-country-phone-dpdn"
              selectedCountry={
                countriesWithState.find(
                  (c) => c.id === formData.phoneCountryId
                ) || new CountryWithStateModel()
              }
              error={
                formData.hasError("phoneCountryId") ||
                formData.hasError("phone")
              }
              errorMessage={"Please select country and enter phone number"}
              onCountryChange={(id: string) => {
                updateFormData("phoneCountryId", id);
                updateFormData(
                  "phoneCountryCode",
                  countriesWithState
                    .find((c) => c.id === id)
                    ?.code?.toString() || ""
                );
              }}
              phoneNumber={formData.phone}
              onPhoneNumberChange={(number: string) =>
                updateFormData("phone", number)
              }
              availableCountries={countriesWithState}
            />
          </FullWidthFormGroup>
          {/* {formData.receiveNotifications && (
          <DisclaimerText style={{ marginTop: '-4px', marginBottom: '8px' }}>
            I agree to opt-in to receive text and email communication. I may opt-out at any
            time. For text messages reply STOP to end. Message and data rates may apply.
          </DisclaimerText>
        )} */}

          <div style={{ display: "flex", justifyContent: "center" }}>
            <JoinButton
              type="submit"
              onClick={() => onSubmitClick()}
              // disabled={!isFormValid()}
              style={{
                width: "50%",
                background: "#10b981",
                color: "#ffffff",
                padding: "18px 32px",
                border: "none",
                borderRadius: "12px",
                fontSize: "18px",
                fontWeight: "700",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              // onMouseEnter={(e) => {
              //   if (!isFormValid()) return;
              //   e.currentTarget.style.background = '#059669';
              // }}
              // onMouseLeave={(e) => {
              //   if (!isFormValid()) return;
              //   e.currentTarget.style.background = '#10b981';
              // }}
            >
              Join Us!
            </JoinButton>
          </div>
        </FormBody>
      </FormContainer>
    );
  }
);
